import { useEffect, useState } from "react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { BellRinging, X, ShieldCheck } from "@phosphor-icons/react";

const DISMISS_KEY = "llm.push.prompt.dismissedAt";
const DISMISS_DAYS = 7; // If dismissed, don't nag again for a week

/**
 * NotificationPermissionPrompt — auto-prompts client users to enable push
 * notifications on first login (or first login after installing the PWA).
 *
 * Won't show if:
 *  - push isn't supported (older browsers, iOS Safari without PWA install)
 *  - permission is already "granted" or "denied"
 *  - user dismissed it in the last DISMISS_DAYS
 */
export default function NotificationPermissionPrompt() {
  const { supported, promptable, subscribe, busy } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!supported || !promptable) return;
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const ageDays = (Date.now() - Number(dismissedAt)) / 86400000;
      if (ageDays < DISMISS_DAYS) return;
    }
    // Small delay so it doesn't fight page load / other modals
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [supported, promptable]);

  const enable = async () => {
    setStatus("");
    const r = await subscribe();
    if (r?.ok) {
      setStatus("granted");
      setTimeout(() => setVisible(false), 1500);
    } else if (r?.reason === "denied") {
      setStatus("denied");
    } else {
      setStatus("error");
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-0 sm:bottom-6 sm:inset-x-auto sm:right-6 z-40 sm:max-w-md p-4 sm:p-0"
      data-testid="push-permission-prompt"
    >
      <div className="bg-[#0F0F0F] border border-[#FF3333]/60 shadow-[0_16px_40px_rgba(0,0,0,0.6)] p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-[#FF3333]/60 bg-[#FF3333]/10 flex items-center justify-center shrink-0">
              <BellRinging size={20} weight="fill" className="text-[#FF3333]" />
            </div>
            <div>
              <div className="id-label text-[#FF3333] mb-0.5">— Notifications —</div>
              <div className="font-oswald text-lg text-white uppercase tracking-wide leading-tight">
                Turn on push?
              </div>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-zinc-500 hover:text-white shrink-0"
            data-testid="push-prompt-dismiss"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <p className="text-zinc-300 font-manrope text-sm leading-relaxed mb-4">
          Get instant nudges from Coach Jimmy — <span className="text-white">missed habits, new meal plans,
          weekly recaps, and coach messages</span>. Buzz-only stuff. No spam. Turn it off anytime.
        </p>

        {status === "denied" && (
          <div className="border border-zinc-700 bg-zinc-900 p-3 mb-3 text-xs font-manrope text-zinc-400 flex items-start gap-2" data-testid="push-status-denied">
            <ShieldCheck size={14} weight="fill" className="mt-0.5 shrink-0" />
            <span>You blocked notifications for this site. Enable them from your browser settings, then refresh.</span>
          </div>
        )}
        {status === "error" && (
          <div className="border border-zinc-700 bg-zinc-900 p-3 mb-3 text-xs font-manrope text-zinc-400" data-testid="push-status-error">
            Couldn&apos;t enable — on iPhone, install the app to your Home Screen first, then try again.
          </div>
        )}
        {status === "granted" && (
          <div className="border border-[#FF3333]/40 bg-[#FF3333]/10 p-3 mb-3 text-xs font-manrope text-[#FF9999]" data-testid="push-status-granted">
            You&apos;re locked in. Coach can now reach you instantly. 🔔
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={dismiss}
            disabled={busy}
            className="id-btn-ghost text-xs border-[#333] text-zinc-400 hover:border-white hover:text-white disabled:opacity-40"
            data-testid="push-prompt-not-now"
          >
            Not now
          </button>
          <button
            onClick={enable}
            disabled={busy || status === "granted"}
            className="id-btn-primary text-xs disabled:opacity-40"
            data-testid="push-prompt-enable"
          >
            <BellRinging size={14} weight="fill" />
            {busy ? "Enabling…" : "Turn on notifications"}
          </button>
        </div>
      </div>
    </div>
  );
}
